// Platform detection + subscription-management routing for Balagha.
//
// We ship the exact same React bundle to web (www.balagha.tech), iOS (Capacitor),
// and Android (Capacitor). But behaviour differs:
//   • On native iOS/Android, digital purchases MUST use Apple IAP / Play Billing.
//   • "Manage Subscription" must route to the ACTUAL purchase provider — NOT the
//     device type. A user who bought via Moyasar on the web and then opens the
//     app on an iPhone should NOT be sent to Apple's subscriptions page.
//
// This helper centralises both device detection and provider-aware routing.

import { LEGAL } from "@/lib/legal";

const w = typeof window !== "undefined" ? window : {};

// Capacitor exposes window.Capacitor when running in the native shell.
const cap = w.Capacitor || null;

/** True when running inside the iOS or Android Capacitor wrapper. */
export const isNativeApp = !!(cap && typeof cap.isNativePlatform === "function"
  ? cap.isNativePlatform()
  : cap?.platform && cap.platform !== "web");

/** "ios" | "android" | "web" */
export const platform = cap?.getPlatform
  ? cap.getPlatform()
  : cap?.platform || "web";

export const isIOS = platform === "ios";
export const isAndroid = platform === "android";

// ---------------------------------------------------------------------------
// Subscription-management routing
// ---------------------------------------------------------------------------
// Return value shape for all helpers below:
//   { kind: "external", url: string }              → open in Browser plugin / new tab
//   { kind: "in_app_cancel" }                       → open the in-app Moyasar
//                                                     cancel-confirm modal
//   { kind: "support", email: string, subject?: string } → open mailto
//   { kind: "none" }                                → do not render the button
//
// The caller (ProfilePage / PricingPage) uses this to decide which action to
// invoke AND whether to show the button at all.

const APPLE_URL = "https://apps.apple.com/account/subscriptions";
const PLAY_URL = "https://play.google.com/store/account/subscriptions";

/**
 * Decide where "Manage Subscription" should go, given the user's subscription
 * record. The decision is driven by `sub.provider` (apple / google / moyasar /
 * revenuecat / unknown) + `sub.active`, NOT by the device type.
 */
export function resolveManageSubscription(sub, { packageName = "com.balagha.app" } = {}) {
  // No active subscription → do not expose the button at all.
  if (!sub || !sub.active) return { kind: "none" };

  const provider = (sub.provider || "").toLowerCase();
  const sku = sub.plan;

  if (provider === "apple") {
    return { kind: "external", url: APPLE_URL };
  }
  if (provider === "google") {
    const q = sku
      ? `?sku=${encodeURIComponent(sku)}&package=${encodeURIComponent(packageName)}`
      : "";
    return { kind: "external", url: `${PLAY_URL}${q}` };
  }
  if (provider === "revenuecat") {
    // RevenueCat routes to the underlying store — resolve by device:
    // iOS always Apple, Android always Play, web shows a note + both links.
    if (isIOS) return { kind: "external", url: APPLE_URL };
    if (isAndroid) {
      const q = sku
        ? `?sku=${encodeURIComponent(sku)}&package=${encodeURIComponent(packageName)}`
        : "";
      return { kind: "external", url: `${PLAY_URL}${q}` };
    }
    // Web: we cannot know which store they bought from. Route to support.
    return {
      kind: "support",
      email: LEGAL.support_email,
      subject: "طلب إدارة اشتراك App Store / Google Play",
    };
  }
  if (provider === "moyasar") {
    // Moyasar has no end-user portal. Our own backend owns the subscription
    // lifecycle. Open the in-app confirm-cancel modal (which calls
    // POST /api/subscription/cancel). Billing continues to the paid period
    // end — then auto-expires.
    return { kind: "in_app_cancel" };
  }
  // Unknown / missing provider → DO NOT default to Apple. Route to support
  // so a human can resolve it correctly.
  return {
    kind: "support",
    email: LEGAL.support_email,
    subject: "طلب إدارة اشتراك — مصدر غير معروف",
  };
}

/**
 * Imperatively execute the resolved action. Returns the resolution so the
 * caller can also render UI (e.g. show the cancel modal) when kind is
 * "in_app_cancel".
 */
export function openManageSubscription(sub, opts = {}) {
  const action = resolveManageSubscription(sub, opts);
  if (action.kind === "external") {
    const browser = w.Capacitor?.Plugins?.Browser;
    if (browser && typeof browser.open === "function") {
      browser.open({ url: action.url }).catch(() => window.open(action.url, "_blank"));
    } else {
      window.open(action.url, "_blank", "noopener,noreferrer");
    }
  } else if (action.kind === "support") {
    const subject = encodeURIComponent(action.subject || "طلب إدارة اشتراك");
    window.location.href = `mailto:${action.email}?subject=${subject}`;
  }
  // "in_app_cancel" and "none" are left to the caller to render.
  return action;
}
